import { cn } from "@/lib/utils";
import { TableHead } from "@/components/ui/table";
import { endOfWeek, eachWeekOfInterval, max, min, format } from "date-fns";
import { DAY_CELL_WIDTH } from "./constants";
import { memo, useMemo } from "react";
import { useLocale } from "@/hooks/useLocale";

interface IProps {
  startDate: Date;
  endDate: Date;
}

const ProjectSchedulesTableWeeklyHeadersComponent = ({
  startDate,
  endDate,
}: IProps) => {
  const locale = useLocale();
  const weekStartsOn = locale.options?.weekStartsOn;
  // Get all weeks that intersect with the date range
  const weeksInRange = useMemo(
    () =>
      eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn }),
    [startDate, endDate, weekStartsOn],
  );

  // Process each week to get the display ranges
  const weekRanges = useMemo(
    () =>
      weeksInRange.map((weekStart) => {
        const weekEnd = endOfWeek(weekStart, { weekStartsOn });
        const displayStart = max([weekStart, startDate]);
        const displayEnd = min([weekEnd, endDate]);
        // Check if start and end dates are in the same month
        const sameMonth = displayStart.getMonth() === displayEnd.getMonth();

        // Calculate days in this display range
        const daysInRange =
          Math.floor(
            (displayEnd.getTime() - displayStart.getTime()) /
              (1000 * 60 * 60 * 24),
          ) + 1; // Add 1 to include both the start and end dates in the count (inclusive range)

        // Format label based on whether it's a single day or a range
        let label;
        // For single day, leave the label empty
        if (daysInRange === 1) {
          label = ""; // Empty string for single days
        } else if (sameMonth) {
          // Same month with multiple days: "10 - 16 lut"
          label = `${format(displayStart, "d", { locale })} - ${format(displayEnd, "d", { locale })} ${format(displayEnd, "MMM", { locale }).toLowerCase()}`;
        } else {
          // Different months: "27 lut - 5 mar"
          label = `${format(displayStart, "d", { locale })} ${format(displayStart, "MMM", { locale }).toLowerCase()} - ${format(displayEnd, "d", { locale })} ${format(displayEnd, "MMM", { locale }).toLowerCase()}`;
        }

        return { label, daysInRange };
      }),
    [weeksInRange, weekStartsOn, startDate, endDate, locale],
  );

  return (
    <>
      {weekRanges.map((week, index) => {
        const cellWidth = week.daysInRange * DAY_CELL_WIDTH;

        return (
          <TableHead
            key={`week-${index}`}
            className={cn(`p-0 text-center `, {
              "border-r border-solid border-white":
                index < weekRanges.length - 1,
            })}
            style={{ minWidth: `${cellWidth}px` }}
          >
            {week.label}
          </TableHead>
        );
      })}
    </>
  );
};

export const ProjectSchedulesTableWeeklyHeaders = memo(
  ProjectSchedulesTableWeeklyHeadersComponent,
);
